import React, { forwardRef, useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { observer } from "mobx-react";
import Button from "ui/Button/Button";
import ProductPreviewImage from "ui/ProductPreviewImage/ProductPreviewImage";
import AddToFavorites from "common/AddToFavorites/AddToFavorites";
import Rating from "common/Rating/Rating";
import Price from "common/Price/Price";
import AddToCart from "common/AddToCart/AddToCart";
import DeliveryBlock from "common/ProductPreview/components/DeliveryBlock/DeliveryBlock";
import Badges from "common/Badges/Badges";
import { useStore } from "store";
import { useUrl } from "helpers/hooks";
import { formatDate } from "helpers/date";
import { cn } from "helpers/style";
import { withTrailSlash } from "helpers/url";
import { ROUTE_CART } from "constants/routes";
import { Props as PreviewProps, CommonProps } from "../../ProductPreview";
import BundleList from "../BundleList/BundleList";
import Prices from "../Prices/Prices";
import styles from "./Horizontal.module.scss";

import HorizontalInfoListWrapper from "./components/HorizontalInfoList/HorizontalInfoList";

type Props = PreviewProps &
  CommonProps & {
    wide?: boolean;
    product: Entity.Product;
  };

const Horizontal = forwardRef<HTMLDivElement, Props>(
  (
    {
      showExpiringPrices,
      product,
      imageSrc,
      showReviewCount,
      analyticsMeta,
      onClick,
      onOpenNotify,
      hideCartToast,
      onInteract,
      disableLazyImage,
      pricePrefix,
      addCartText,
      isEmployeeMode,
      prices,
      price,
      withSKU,
      isNewPricePrefix,
    },
    ref
  ) => {
    const { cart } = useStore();
    const productInCart = cart.get(product.id, price);
    const [currentPrice, setCurrentPrice] = useState(price);
    const isAvailable =
      ((product.availableForOrder || product.availableForBooking) &&
        Boolean(price)) ||
      (price?.isExpirating && Boolean(price?.price) && showExpiringPrices);
    const limited = false;
    /* todo: check this later, weird behaviour of SWC (minification?) here, && operator */
    const withLimitedLabel: boolean = limited && Boolean(productInCart);
    const { getEntityLinkProps } = useUrl();

    const onClickHandler = useCallback(
      () => onClick && onClick({ product }),
      [onClick, product]
    );

    useEffect(() => {
      setCurrentPrice(price);
    }, [price]);

    const showRating = Boolean(
      (!showReviewCount && product.rating) ||
        (showReviewCount && product.rating && product.reviewsCount)
    );
    const showPrices =
      prices?.length > 1 && (showExpiringPrices || isEmployeeMode);

    const classNamePrice =
      !isNewPricePrefix && styles.horizontalPriceContainerPriceCurrency;

    return (
      <div ref={ref}>
        <div
          className={styles.horizontalWrapper}
          data-id={product.id}
          data-diginetica="produc_special_diginetica"
        >
          <div className={styles.horizontalInner}>
            <div className={styles.horizontalUpperActions}>
              <AddToFavorites product={product} />
            </div>

            <div className={styles.horizontalUpper}>
              <ProductPreviewImage
                onClick={onClickHandler}
                onMouseDown={onInteract}
                linkProps={getEntityLinkProps("product", product)}
                src={imageSrc}
                ariaLabel={product.name}
                type="horizontal"
                priority={disableLazyImage}
                title={product.name}
                alt={product.name}
              />
              {product.badges && product.badges.length > 0 && (
                <div className={styles.horizontalBadges}>
                  <Badges
                    badges={product.badges}
                    tooltipProps={{
                      maxWidth: 292,
                      placement: "top-start",
                    }}
                    alt="Шильдик акции"
                  />
                </div>
              )}
            </div>

            <div className={styles.horizontalContent}>
              <div className={styles.horizontalContentInfo}>
                {showRating && (
                  <div className={styles.horizontalRating}>
                    <Rating
                      value={product.rating}
                      label={showReviewCount && product.reviewsCount}
                    />
                  </div>
                )}

                <Link
                  {...getEntityLinkProps("product", product)}
                  className={styles.horizontalTitle}
                  passHref
                >
                  <span
                    onClick={() => onClick && onClick({ product })}
                    onMouseDown={onInteract}
                    title={product.name}
                  >
                    {product.name}
                  </span>
                </Link>

                {product.isBundle && (
                  <BundleList
                    className={styles.horizontalBundleList}
                    items={product.bundleItemsSimple}
                    link={getEntityLinkProps("product", product)}
                  />
                )}

                <div className={styles.horizontalDescriptionContainer}>
                  <span
                    className={cn([
                      styles.horizontalAvailability,
                      isAvailable && styles.horizontalAvailabilityAvailable,
                    ])}
                  />

                  {!showPrices && currentPrice?.dateExpired && (
                    <span className={styles.horizontalExpirationTime}>
                      Годен до{" "}
                      {formatDate(currentPrice?.dateExpired, "DD.MM.YYYY")}
                    </span>
                  )}

                  {price?.dateExpiredOpened && (
                    <span className={styles.horizontalExpirationDate}>
                      После вскрытия — {price?.dateExpiredOpened}
                    </span>
                  )}

                  {withSKU && product.sku && (
                    <span className={styles.horizontalSku}>
                      Артикул: {product.sku}
                    </span>
                  )}
                </div>

                <HorizontalInfoListWrapper
                  list={[
                    {
                      label: "Действующее вещество (МНН)",
                      text: product.mnns?.map(i => i.title).join(", "),
                    },
                    {
                      label: "Производитель",
                      text: product.producer,
                    },
                    {
                      label: "Категория",
                      text: product.category.name,
                    },
                    {
                      label: "Страна производства",
                      text: product.producerCountry,
                    },
                  ]}
                />
              </div>

              <div
                className={cn([
                  styles.horizontalActions,
                  !isAvailable && styles.horizontalActionsNotAvailable,
                  isEmployeeMode &&
                    isAvailable &&
                    styles.horizontalActionsRelative,
                ])}
              >
                {(product.deliveryDate ||
                  product.pickupDate ||
                  product.availableForBooking) &&
                  isAvailable &&
                  !productInCart && (
                    <DeliveryBlock horizontal product={product} />
                  )}

                <div className={styles.horizontalAddToCartContainer}>
                  <div className={styles.horizontalPriceContainer}>
                    {showPrices ? (
                      <Prices
                        product={product}
                        className={styles.horizontalPrices}
                        prices={prices}
                        onPriceChange={setCurrentPrice}
                        activePrice={currentPrice}
                        pricePrefix={pricePrefix}
                        isAvailable={isAvailable}
                        showExpiryDate={!isEmployeeMode}
                        classNamePriceCurrency={classNamePrice}
                        isNewPriceStyle={isNewPricePrefix}
                      />
                    ) : (
                      <div>
                        {!isAvailable && product.lastPrice !== 0 && (
                          <Price
                            price={product.lastPrice}
                            prefix={pricePrefix}
                            className={styles.horizontalLastPrice}
                            classNamePrice={classNamePrice}
                            isNewPriceStyle={isNewPricePrefix}
                          />
                        )}
                        <Price
                          hasOnlyBookingPrices={product.hasOnlyBookingPrices}
                          hasPrefix={currentPrice?.hasPrefix}
                          price={currentPrice?.price || 0}
                          priceOld={currentPrice?.priceOld}
                          discount={currentPrice?.discount}
                          prefix={pricePrefix}
                          classNamePrice={classNamePrice}
                          isNewPriceStyle={isNewPricePrefix}
                        />
                      </div>
                    )}
                  </div>

                  <div className={styles.horizontalAdditionalActions}>
                    {isAvailable && (
                      <div className={styles.horizontalAddToCartWrapper}>
                        {withLimitedLabel && (
                          <div className={styles.horizontalLimitedLabel} />
                        )}

                        <AddToCart
                          product={product}
                          price={currentPrice}
                          size="large"
                          counterWide
                          counterSize="large"
                          analyticsMeta={analyticsMeta}
                          hideCartToast={hideCartToast}
                          text={addCartText}
                          showExpiringPrices={
                            showExpiringPrices || isEmployeeMode
                          }
                          className={styles.horizontalAddToCart}
                        />
                      </div>
                    )}

                    {!isAvailable && (
                      <div className={styles.horizontalNotAvailableContainer}>
                        <Button orange outlined fluid onClick={onOpenNotify} />
                      </div>
                    )}

                    {isAvailable && productInCart && (
                      <a
                        href={withTrailSlash(ROUTE_CART)}
                        className={styles.horizontalAdditionalLink}
                      >
                        Перейти в Корзину
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {product.isAdv && product.warning && (
            <div className={styles.horizontalDisclaimer}>{product.warning}</div>
          )}
        </div>
      </div>
    );
  }
);

export default observer(Horizontal);
